import { useCallback, useEffect, useMemo } from "react";

import {
  AddBusinessOutlined,
  ApartmentOutlined,
  CurrencyExchangeOutlined,
  FeedOutlined,
  FolderOpenOutlined,
  HouseOutlined,
  LocalGroceryStoreOutlined,
  LogoutOutlined,
  PersonOutline,
} from "@mui/icons-material";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { useStytch } from "@stytch/react";
import { isBoolean, isEmpty, isNil } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../_src_shared/DashboardLayout";

import Flx from "../components/layout/Flx";
import DashboardSidebar from "../components/sidebars/DashboardSidebar/DashboardSidebar";
import { useEndpointHook } from "../hooks/useEndpointHook";
import DevToolsPanel from "../testing/Devtoolspanel";
import ProfileSetupWizard from "./ProfileSetupWizard";
import AgUiBorrowerAssistantChat from "../components/agui/AgUiBorrowerAssistantChat/AgUiBorrowerAssistantChat";

const AuthenticatedDashboard = ({ children }) => {
  const stytch = useStytch();
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getProfile, getBorrowerFinancingApplications } = useEndpointHook();
  // const { getBorrowerProfile } = useBorrowerPortalHook();
  // const { getBorrowerProfile } = useBorrowerPortalHook();

  // Redux state
  const profile = useSelector((state) => state.profile);
  const financingRequests = useSelector((state) => state.financingRequests);
  const navSidebarOpen = useSelector((state) => state?.navSidebar?.open);

  const open = useMemo(
    () => navSidebarOpen || isMobile,
    [navSidebarOpen, isMobile],
  );

  const handleLogoutUser = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/");
  }, [stytch, navigate]);

  useEffect(() => {
    if (isNil(profile)) {
      console.log("Root - Fetching profile...");
      getProfile();
    }
  }, [profile]);

  useEffect(() => {
    if (isNil(financingRequests)) {
      console.log("Root -Fetching financing requests...");
      getBorrowerFinancingApplications();
    }
  }, [financingRequests]);
  // useEffect(() => {
  //   if (isNil(profile)) {
  //     console.log("Root - Fetching profile...");
  //     getProfile();
  //   }
  //   if (isNil(financingRequests) && !isNil(profile)) {
  //     console.log("Root -Fetching financing requests...");
  //     getBorrowerFinancingApplications();
  //   }
  // }, [profile]);

  return (
    <DashboardWrapper onLogoutClick={handleLogoutUser} open={open}>
      {/* {process.env.NODE_ENV === "development" && <DevToolsPanel />} */}
      <ContentRenderer>
        {
          <>
            {children}
            <DashboardSidebar />
            <AgUiBorrowerAssistantChat />
            {/* <BorrowerAssistantChatSimple /> */}

            {/* <CopilotKitProvider>
              {children}
              <DashboardSidebar />
              <BorrowerAssistantCopilot />
            </CopilotKitProvider> */}
          </>
        }
      </ContentRenderer>
    </DashboardWrapper>
  );
};

const ContentRenderer = ({ children }) => {
  const profile = useSelector((state) => state.profile);

  const profileComplete = useMemo(() => {
    // Trigger setup modal
    if (profile?.middleName === "test") {
      return false;
    }

    // Trigger setup modal
    if (
      isNil(profile?.firstName) ||
      isNil(profile?.lastName) ||
      isNil(profile?.dateOfBirth)
    ) {
      return false;
    }

    // Skip setup modal and show content
    if (isBoolean(profile?.profileComplete) && profile?.profileComplete) {
      return true;
    }
    return false;
  }, [profile]);

  if (isNil(profile)) {
    return (
      <Flx fw sx={{ height: "100vh" }} center>
        <CircularProgress />
      </Flx>
    );
  }

  return profileComplete ? children : <ProfileSetupWizard profile={profile} />;
};

const DashboardWrapper = ({ open, onLogoutClick, children }) => {
  const financingRequests = useSelector((state) => state.financingRequests);
  const topLinks = useMemo(() => {
    // Default links for normal resilender borrowers
    let defaultLinks = [
      {
        icon: <HouseOutlined className="thin" />,
        to: "/",
        label: "Home",
      },

      {
        icon: <AddBusinessOutlined />,
        to: "/financing-request/create",
        label: "New Financing Request",
      },
    ];
    if (isEmpty(financingRequests)) {
      return [
        {
          items: [
            ...defaultLinks,
            {
              icon: <LocalGroceryStoreOutlined />,
              to: "/off-market-listings",
              label: "Off-Market Listings",
            },
          ],
        },
      ];
    }
    return [
      {
        items: [
          ...defaultLinks,
          {
            icon: <FeedOutlined />,
            to: "/financing-requests",
            label: "My Financing Requests",
          },
          {
            icon: <CurrencyExchangeOutlined />,
            to: "/draw-request",
            label: "New Draw Request",
          },
          {
            icon: <LocalGroceryStoreOutlined />,
            to: "/off-market-listings",
            label: "Off-Market Listings",
          },
        ],
      },
    ];
  }, [financingRequests]);

  const bottomLinks = useMemo(
    () => [
      {
        items: [
          // {
          //   icon: <GroupOutlined className="thin" />,
          //   to: "/registered-users",
          //   label: "Registered Users",
          // },
          {
            icon: <ApartmentOutlined className="thin" />,
            to: "/profile/entities",
            label: "Associated Entities & LLC's",
          },
          {
            icon: <FolderOpenOutlined className="thin" />,
            to: "/profile/files",
            label: "Document Library",
          },
          {
            icon: <PersonOutline className="thin" />,
            to: "/profile",
            label: "Profile",
          },
          {
            type: "logout",
            label: "Log Out",
            onClick: onLogoutClick,
            icon: <LogoutOutlined sx={{ transform: "rotate(180deg)" }} />,
          },
        ],
      },
    ],
    [],
  );
  return (
    <DashboardLayout topLinks={topLinks} bottomLinks={bottomLinks} open={open}>
      {children}
    </DashboardLayout>
  );
};

/**
 * TEST DATA
 */

export default AuthenticatedDashboard;
